import React, { type ReactElement, type ReactNode } from "react";
import { MirrorNotice } from "../appointments/mirror-mark";
import { Button, Input, SheetClose, SheetTitle, cn } from "@ndd/ui";
import { Copy, Repeat, Video, X } from "lucide-react";
import type { RdScheduleAppointment } from "../../../../shared/rd-schedule";
import {
  TAG_LABELS,
  aheadLine,
  chartNoteLine,
  clientSinceLine,
  detailsGroup,
  firstNameOf,
  feeCopy,
  noNextBody,
  patientPagePath,
  recurringLine,
  safeJoinUrl,
  statusLine,
  typeLine,
  whenLine,
  type StatusTone,
} from "../../lib/rd-schedule-details";
import { isNoteEditable } from "../../lib/rd-schedule-notes";
import { Eyebrow } from "../v2/eyebrow";
import type { ActionOptions, ActionOptionsState } from "./actions/use-action-options";

// NO-875. The appointment details panel's content (spec § Details panel, the
// canvas's Details, DetailsNoNext and DetailsLate artboards). A leaf module:
// no router, query client or `@/` import, so `tsx --test` renders it. The
// Sheet frame lives in appointment-details-panel.tsx, which Radix portals out
// of a static render. Every action is a callback; the dialogs they open come
// from NO-876.

/** The Note section shows for a visit whose note can be edited: the server's actionable.edit (scheduled or checked in). */
export function showsNotes(appointment: RdScheduleAppointment): boolean {
  return isNoteEditable(appointment);
}

/** The footer and section actions. NO-874 and NO-876 supply them. */
export interface AppointmentDetailsActions {
  /** Opens New appointment with the Client set (Book next visit). */
  onBookNextVisit: (appointment: RdScheduleAppointment) => void;
  /** Hands off to Healthie charting for now. */
  onAddChartNote: (appointment: RdScheduleAppointment) => void;
  onReschedule: (appointment: RdScheduleAppointment) => void;
  /** Opens Edit note; the Note section's Edit. */
  onEditDetails: (appointment: RdScheduleAppointment) => void;
  onCancelOrMarkMissed: (appointment: RdScheduleAppointment) => void;
  onDelete: (appointment: RdScheduleAppointment) => void;
  onChangeStatus: (appointment: RdScheduleAppointment) => void;
  /** NO-917: sends a failed copy to Healthie again; absent when the action is not known. */
  onRetryMirror?: (appointment: RdScheduleAppointment) => Promise<void>;
}

/**
 * The write behind "Charge the fee anyway" and "Waive". The record of the RD's
 * decision needs the appointment write API (NO-872); until it is wired,
 * `available` is false and the buttons render disabled with a note.
 */
export interface FeeDecisionPort {
  available: boolean;
  pending?: boolean;
  onDecide: (appointment: RdScheduleAppointment, decision: "charge" | "waive") => void;
}

export const FEE_DECISION_UNAVAILABLE: FeeDecisionPort = {
  available: false,
  onDecide: () => undefined,
};

export type RenderPatientLink = (input: {
  patientId: string;
  path: string;
  className: string;
  children: ReactNode;
}) => ReactElement;

const DEFAULT_PATIENT_LINK: RenderPatientLink = ({ path, className, children }) => (
  <a href={path} className={className}>
    {children}
  </a>
);

const TONE_INK: Record<StatusTone, string> = {
  success: "text-success",
  danger: "text-danger",
  warning: "text-warning",
  muted: "text-muted-strong",
};

const DOT_FILL: Record<RdScheduleAppointment["calendarStatus"], string> = {
  upcoming: "bg-card shadow-[inset_0_0_0_1.5px_var(--foreground)]",
  done: "bg-success",
  cancelled: "bg-warning",
  rescheduled: "bg-muted-foreground",
  late_cancel: "bg-danger",
  no_show: "bg-danger",
};

const SECTION = "flex flex-col gap-2.5 border-b border-border px-6 py-5";

interface PartProps {
  appointment: RdScheduleAppointment;
  timezone: string;
}

function TypeLine({
  appointment,
}: {
  appointment: RdScheduleAppointment;
}): ReactElement {
  return (
    <span className="inline-flex items-center gap-2 text-label text-muted-strong">
      <span
        aria-hidden="true"
        className={cn("size-2.5 rounded-full", DOT_FILL[appointment.calendarStatus])}
      />
      {typeLine(appointment)}
      {appointment.tags.map((tag) => (
        <span
          key={tag}
          className="rounded-sm border border-border-strong px-1.5 py-px font-mono text-micro font-medium tracking-wider text-foreground"
        >
          {TAG_LABELS[tag]}
        </span>
      ))}
    </span>
  );
}

function NoNextVisitNote({
  appointment,
  onBook,
}: {
  appointment: RdScheduleAppointment;
  onBook: () => void;
}): ReactElement {
  return (
    <div
      role="note"
      className="mt-2 flex flex-col gap-2.5 rounded-lg bg-danger-soft px-4 py-3.5"
    >
      <span className="flex items-center gap-2 font-semibold text-danger">
        <span
          aria-hidden="true"
          className="grid size-[18px] place-items-center rounded-full bg-danger font-sans text-micro font-extrabold leading-none text-white"
        >
          !!
        </span>
        No next visit booked
      </span>
      <span className="text-body-sm">{noNextBody(appointment)}</span>
      <Button
        type="button"
        variant="press"
        size="compact"
        shape="pill"
        className="self-start"
        onClick={onBook}
      >
        Book next visit
      </Button>
    </div>
  );
}

// The server sets `noNextVisit` per Client, so every appointment of a Client
// in the week carries it. The note belongs only to the visit with nothing after.
function showsNoNextVisitNote(appointment: RdScheduleAppointment): boolean {
  return appointment.noNextVisit && appointment.followingVisitAt === null;
}

function RecurringLine({ text }: { text: string }): ReactElement {
  return (
    <p className="mt-0.5 flex items-center gap-2.5 font-semibold">
      <span
        aria-hidden="true"
        className="grid size-6 flex-none place-items-center rounded-full bg-primary text-primary-foreground"
      >
        <Repeat className="size-3.5" strokeWidth={3} />
      </span>
      {text}
    </p>
  );
}

function DetailsHeader({
  appointment,
  timezone,
  onBookNextVisit,
  onRetryMirror,
}: PartProps & {
  onBookNextVisit: () => void;
  onRetryMirror?: () => Promise<void>;
}): ReactElement {
  const status = statusLine(appointment);
  const recurring = recurringLine(appointment);
  const ahead = aheadLine(appointment, timezone);

  return (
    <header className="flex flex-col gap-2 border-b border-border px-6 pb-5 pt-[22px]">
      <div className="flex items-center justify-between gap-2">
        <TypeLine appointment={appointment} />
        <SheetClose asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Close details"
            className="text-muted-strong"
          >
            <X aria-hidden="true" />
          </Button>
        </SheetClose>
      </div>
      <SheetTitle
        data-drawer-title
        className="font-sans text-heading-sm font-bold leading-title text-foreground"
      >
        {appointment.client.name ?? "Client"}
      </SheetTitle>
      <p className="text-body-lg">{whenLine(appointment, timezone)}</p>
      {status ? (
        <p className={cn("text-body-sm font-semibold", TONE_INK[status.tone])}>
          {status.text}
        </p>
      ) : null}
      <MirrorNotice mirrorState={appointment.mirrorState} onRetry={onRetryMirror} />
      {recurring ? <RecurringLine text={recurring} /> : null}
      {ahead ? <p className="text-body-sm text-muted-strong">{ahead}</p> : null}
      {showsNoNextVisitNote(appointment) ? (
        <NoNextVisitNote appointment={appointment} onBook={onBookNextVisit} />
      ) : null}
    </header>
  );
}

function FeeAction({
  action,
  fee,
  onDecide,
}: {
  action: { kind: "charge" | "waive"; label: string };
  fee: FeeDecisionPort;
  onDecide: (kind: "charge" | "waive") => void;
}): ReactElement {
  return (
    <>
      <Button
        type="button"
        variant="utility"
        size="compact"
        className="self-start"
        disabled={!fee.available || fee.pending === true}
        onClick={() => onDecide(action.kind)}
      >
        {action.label}
      </Button>
      {fee.available ? null : (
        <span className="text-label text-muted-foreground">
          Recording a fee decision isn&apos;t available yet.
        </span>
      )}
    </>
  );
}

function FeeSection({
  appointment,
  fee,
}: {
  appointment: RdScheduleAppointment;
  fee: FeeDecisionPort;
}): ReactElement | null {
  if (!appointment.fee) return null;

  const copy = feeCopy(appointment.fee, appointment);

  return (
    <section aria-labelledby="details-fee" className={SECTION}>
      <Eyebrow as="h3">
        <span id="details-fee">Fee</span>
      </Eyebrow>
      <span className="flex flex-col gap-0.5">
        <span className="font-semibold">{copy.title}</span>
        <span className="text-body-sm text-muted-strong">{copy.body}</span>
      </span>
      {copy.action ? (
        <FeeAction
          action={copy.action}
          fee={fee}
          onDecide={(kind) => fee.onDecide(appointment, kind)}
        />
      ) : null}
    </section>
  );
}

function VideoSection({
  joinUrl,
  onCopyLink,
}: {
  joinUrl: string | null;
  onCopyLink: (url: string) => void;
}): ReactElement {
  return (
    <section aria-labelledby="details-video" className={SECTION}>
      <Eyebrow as="h3">
        <span id="details-video">Video call</span>
      </Eyebrow>
      {joinUrl ? (
        <>
          <Button asChild variant="press" shape="pill" className="h-10 text-title-sm">
            <a href={joinUrl} target="_blank" rel="noopener noreferrer">
              <Video aria-hidden="true" />
              Join video call
            </a>
          </Button>
          <label
            htmlFor="details-client-link"
            className="mt-1 text-label font-semibold"
          >
            Client&apos;s link
          </label>
          <div className="flex gap-2">
            <Input
              id="details-client-link"
              readOnly
              value={joinUrl}
              className="h-9 min-w-0 flex-1 bg-muted font-mono text-label text-muted-strong"
            />
            <Button
              type="button"
              variant="utility"
              size="compact"
              className="h-9"
              onClick={() => onCopyLink(joinUrl)}
            >
              <Copy aria-hidden="true" />
              Copy link
            </Button>
          </div>
        </>
      ) : (
        <span className="text-reading text-muted-strong">
          No video link for this visit yet.
        </span>
      )}
    </section>
  );
}

function DocumentationSection({
  appointment,
  onAddChartNote,
}: {
  appointment: RdScheduleAppointment;
  onAddChartNote: () => void;
}): ReactElement {
  return (
    <section aria-labelledby="details-docs" className={SECTION}>
      <Eyebrow as="h3">
        <span id="details-docs">Documentation</span>
      </Eyebrow>
      <div className="flex items-center gap-3">
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="font-medium">Chart note</span>
          <span className="text-label text-muted-foreground">
            {chartNoteLine(appointment)}
          </span>
        </span>
        <Button type="button" variant="utility" size="compact" onClick={onAddChartNote}>
          Add chart note
        </Button>
      </div>
    </section>
  );
}

/** The note a read returned, null when it has none. */
function noteRead(read: ActionOptions["current"] | null): string | null {
  return read?.privateNote ?? null;
}

function NoteSkeleton(): ReactElement {
  return (
    <div aria-hidden="true" className="flex flex-col gap-2">
      <span className="h-3.5 w-2/3 rounded-sm bg-muted" />
      <span className="h-3.5 w-1/2 rounded-sm bg-muted" />
    </div>
  );
}

function NoteUnavailable({ onReload }: { onReload: () => void }): ReactElement {
  return (
    <div className="flex items-center gap-2 text-reading text-muted-foreground">
      <span>Note unavailable right now.</span>
      <Button
        type="button"
        variant="quiet-link"
        size="compact"
        className="text-primary"
        onClick={onReload}
      >
        Try again
      </Button>
    </div>
  );
}

/** The read failed, or Healthie did not return the note. */
function noteIsUnavailable(notes: ActionOptionsState): boolean {
  if (notes.status === "error") return true;

  return notes.status === "ready" && !notes.options.current.privateNoteAvailable;
}

function NotesBody({
  appointment,
  notes,
  savingNote,
  onReload,
}: {
  appointment: RdScheduleAppointment;
  notes: ActionOptionsState;
  /** The note the RD just saved, while Healthie has not returned it yet. */
  savingNote: string | null;
  onReload: () => void;
}): ReactElement | null {
  const first = firstNameOf(appointment.client.name, "the Client");
  const read = notes.status === "ready" ? notes.options.current : null;
  const unavailable = noteIsUnavailable(notes);
  const text = savingNote ?? noteRead(read);
  const shown = text?.trim() ? text : null;

  if (savingNote === null && notes.status === "loading") {
    return <NoteSkeleton />;
  }

  if (savingNote === null && unavailable) {
    return <NoteUnavailable onReload={onReload} />;
  }

  if (!shown) {
    return (
      <>
        <span className="text-reading text-muted-foreground">
          No note for this visit.
        </span>
        {savingNote !== null ? <SavingLabel /> : null}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      <p className="line-clamp-3 whitespace-pre-line text-reading text-muted-strong">
        {shown}
      </p>
      <span className="text-body text-muted-foreground">{first} doesn't see this</span>
      {savingNote !== null ? <SavingLabel /> : null}
    </div>
  );
}

function SavingLabel(): ReactElement {
  return <span className="text-body text-muted-foreground">Saving to Healthie…</span>;
}

function NotesSection({
  appointment,
  notes,
  savingNote,
  onEdit,
  onReload,
}: {
  appointment: RdScheduleAppointment;
  notes: ActionOptionsState;
  savingNote: string | null;
  onEdit: () => void;
  onReload: () => void;
}): ReactElement | null {
  // The server says whether the visit can still be edited.
  if (notes.status === "ready" && !notes.options.actionable.edit) return null;

  // Edit shows exactly when the dialog can edit: not while the body offers only Try again.
  // Loading and a pending save keep it, as the body shows the saving note then.
  const canEdit = savingNote !== null || !noteIsUnavailable(notes);

  return (
    <section aria-labelledby="details-notes" className={SECTION}>
      <div className="flex items-center justify-between gap-2">
        <Eyebrow as="h3">
          <span id="details-notes">Note</span>
        </Eyebrow>
        {canEdit ? (
          <Button
            type="button"
            variant="quiet-link"
            size="compact"
            className="text-primary"
            aria-label="Edit note"
            onClick={onEdit}
          >
            Edit
          </Button>
        ) : null}
      </div>
      {notes.status === "loading" && savingNote === null ? (
        <span className="sr-only">Loading note</span>
      ) : null}
      <NotesBody
        appointment={appointment}
        notes={notes}
        savingNote={savingNote}
        onReload={onReload}
      />
    </section>
  );
}

function ClientSection({
  appointment,
  timezone,
  patientDetailEnabled,
  renderPatientLink,
}: PartProps & {
  patientDetailEnabled: boolean;
  renderPatientLink: RenderPatientLink;
}): ReactElement {
  return (
    <section aria-labelledby="details-client" className="flex flex-col gap-1 px-6 py-5">
      <Eyebrow as="h3" className="mb-1.5">
        <span id="details-client">Client</span>
      </Eyebrow>
      <span className="font-medium">{appointment.client.name ?? "Client"}</span>
      <span className="text-label text-muted-foreground">
        {clientSinceLine(appointment, timezone)}
      </span>
      {patientDetailEnabled
        ? renderPatientLink({
            patientId: appointment.client.patientId,
            path: patientPagePath(appointment.client.patientId),
            className:
              "self-start text-reading font-semibold text-primary underline decoration-1 underline-offset-[3px]",
            children: "Open patient page",
          })
        : null}
    </section>
  );
}

function FooterButton({
  children,
  onClick,
  className,
}: {
  children: ReactNode;
  onClick: () => void;
  className?: string;
}): ReactElement {
  return (
    <Button
      type="button"
      variant="utility"
      className={cn("h-[38px]", className)}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function DetailsFooter({
  appointment,
  actions,
}: {
  appointment: RdScheduleAppointment;
  actions: AppointmentDetailsActions;
}): ReactElement {
  const upcoming = detailsGroup(appointment) === "upcoming";

  return (
    <footer className="mt-auto flex flex-col gap-2.5 border-t border-border px-6 pb-[22px] pt-4">
      <Eyebrow as="h3">This appointment</Eyebrow>
      {upcoming ? (
        <>
          <div className="flex flex-col gap-2.5">
            <FooterButton onClick={() => actions.onReschedule(appointment)}>
              Reschedule
            </FooterButton>
            <FooterButton onClick={() => actions.onCancelOrMarkMissed(appointment)}>
              Cancel or mark missed
            </FooterButton>
          </div>
          <Button
            type="button"
            variant="quiet-link"
            size="compact"
            className="self-center text-muted-foreground"
            onClick={() => actions.onDelete(appointment)}
          >
            Delete appointment
          </Button>
        </>
      ) : (
        <FooterButton onClick={() => actions.onChangeStatus(appointment)}>
          Change status
        </FooterButton>
      )}
    </footer>
  );
}

/** Retry is offered only when the visit names the action to send again. */
function mirrorRetry(
  actions: AppointmentDetailsActions,
  appointment: RdScheduleAppointment
): (() => Promise<void>) | undefined {
  const retry = actions.onRetryMirror;

  if (!retry || !appointment.mirrorActionId) return undefined;

  return () => retry(appointment);
}

function ChartNoteSection({
  appointment,
  actions,
}: {
  appointment: RdScheduleAppointment;
  actions: AppointmentDetailsActions;
}): ReactElement {
  return (
    <DocumentationSection
      appointment={appointment}
      onAddChartNote={() => actions.onAddChartNote(appointment)}
    />
  );
}

export function AppointmentDetailsBody({
  appointment,
  timezone,
  patientDetailEnabled,
  actions,
  fee = FEE_DECISION_UNAVAILABLE,
  notes,
  savingNote = null,
  onReloadNotes = () => undefined,
  onCopyLink,
  renderPatientLink = DEFAULT_PATIENT_LINK,
}: {
  appointment: RdScheduleAppointment;
  /** The week's zone (`RdScheduleWeek.timezone`). */
  timezone: string;
  /** The `patient-detail` flag: Open patient page is hidden while it is off. */
  patientDetailEnabled: boolean;
  actions: AppointmentDetailsActions;
  fee?: FeeDecisionPort;
  /** The visit's note as the action options read it; absent, the Note section is not shown. */
  notes?: ActionOptionsState;
  /** The note the RD just saved, shown with "Saving to Healthie…" until a read returns it. */
  savingNote?: string | null;
  /** Reads the note again (Try again). */
  onReloadNotes?: () => void;
  /** Copies the Client's link and confirms. */
  onCopyLink: (url: string) => void;
  renderPatientLink?: RenderPatientLink;
}): ReactElement {
  const upcoming = detailsGroup(appointment) === "upcoming";
  // A visit that happened still needs its chart note; a missed one has none.
  const documented = upcoming || appointment.calendarStatus === "done";

  return (
    <div className="flex min-h-full flex-col bg-card text-reading text-foreground">
      <DetailsHeader
        appointment={appointment}
        timezone={timezone}
        onBookNextVisit={() => actions.onBookNextVisit(appointment)}
        onRetryMirror={mirrorRetry(actions, appointment)}
      />
      <FeeSection appointment={appointment} fee={fee} />
      {upcoming ? (
        <VideoSection joinUrl={safeJoinUrl(appointment)} onCopyLink={onCopyLink} />
      ) : null}
      {documented ? (
        <ChartNoteSection appointment={appointment} actions={actions} />
      ) : null}
      {notes && showsNotes(appointment) ? (
        <NotesSection
          appointment={appointment}
          notes={notes}
          savingNote={savingNote}
          onEdit={() => actions.onEditDetails(appointment)}
          onReload={onReloadNotes}
        />
      ) : null}
      <ClientSection
        appointment={appointment}
        timezone={timezone}
        patientDetailEnabled={patientDetailEnabled}
        renderPatientLink={renderPatientLink}
      />
      <DetailsFooter appointment={appointment} actions={actions} />
    </div>
  );
}
